import { useEffect, useState } from 'react';
import { PencilIcon, TrashIcon, PlusIcon } from '@bizfirst/common-react';
import type { AgentScopeDTO, AgentScopeFormValues } from '../../types';
import { useAgentScopeAdminApiClient } from '../../context/AiAgentAdminApiContext';
import { ConfirmDialog } from '../Shared/ConfirmDialog';
import { UnverifiedBanner } from '../Shared/UnverifiedBanner';

const EMPTY_FORM: AgentScopeFormValues = { ScopeTypeID: 0 };

/**
 * Restored Scopes tab (Phase 3 of the Agent-editor sub-entity restoration — see
 * TemplatesPanel.tsx/RulesPanel.tsx/HooksPanel.tsx, this panel's direct structural template).
 * Real entity (AIAgent_Scopes) confirmed by reading AgentScope.cs directly: a bare junction row —
 * AgentID + a required ScopeTypeID FK, nothing else, exactly as briefed. No ScopeName/string
 * ScopeType/ScopeDescription/ScopePermissions/IsActive. No lookup controller was found for
 * ScopeTypeID, so it renders as a plain required number input, same treatment RulesPanel.tsx
 * gives AgentRuleTypeID.
 */
export function ScopesPanel({ agentId }: { agentId: number }) {
  const api = useAgentScopeAdminApiClient();
  const [scopes, setScopes] = useState<AgentScopeDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<AgentScopeDTO | 'new' | null>(null);
  const [form, setForm] = useState<AgentScopeFormValues>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<AgentScopeDTO | null>(null);
  const [deleting, setDeleting] = useState(false);

  const load = async () => {
    setLoading(true);
    const result = await api.getScopesByAgent(agentId);
    setScopes(result.data ?? []);
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentId]);

  const startEdit = (scope: AgentScopeDTO | 'new') => {
    setEditing(scope);
    setForm(scope === 'new' ? EMPTY_FORM : { ScopeTypeID: scope.ScopeTypeID });
  };

  const handleSave = async () => {
    if (!form.ScopeTypeID) return;
    setSaving(true);
    const result = editing === 'new' ? await api.createScope(agentId, form) : await api.updateScope((editing as AgentScopeDTO).AgentScopeID, form);
    setSaving(false);
    if (result.success) {
      setEditing(null);
      await load();
    }
  };

  const handleDeleteConfirmed = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    const result = await api.deleteScope(pendingDelete.AgentScopeID);
    setDeleting(false);
    if (result.success) {
      setPendingDelete(null);
      setScopes((prev) => prev.filter((s) => s.AgentScopeID !== pendingDelete.AgentScopeID));
    }
  };

  if (editing) {
    return (
      <div className="aaa-form-grid">
        <UnverifiedBanner entityName="Scopes" />
        <div className="aaa-form-group">
          <label>Scope Type ID *</label>
          <input
            type="number"
            value={form.ScopeTypeID || ''}
            onChange={(e) => setForm((f) => ({ ...f, ScopeTypeID: e.target.value ? parseInt(e.target.value, 10) : 0 }))}
          />
        </div>
        <div className="aaa-form-group-full aaa-editor-footer-actions">
          <button type="button" className="aaa-btn aaa-btn-default" onClick={() => setEditing(null)} disabled={saving}>
            Cancel
          </button>
          <button type="button" className="aaa-btn aaa-btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save Scope'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <UnverifiedBanner entityName="Scopes" />
      <div className="aaa-editor-footer-actions" style={{ marginBottom: 12 }}>
        <button type="button" className="aaa-btn aaa-btn-primary aaa-btn-sm" onClick={() => startEdit('new')}>
          <PlusIcon size={13} /> Add Scope
        </button>
      </div>
      {loading && <div className="aaa-editor-loading">Loading…</div>}
      {!loading && scopes.length === 0 && <div className="aaa-entity-dropdown-empty">No scopes yet.</div>}
      <div className="aaa-entity-grid">
        {scopes.map((scope) => (
          <div key={scope.AgentScopeID} className="aaa-entity-card">
            <div className="aaa-entity-card-header">
              <strong>Scope Type #{scope.ScopeTypeID}</strong>
            </div>
            <div className="aaa-entity-card-actions">
              <button type="button" className="aaa-icon-btn" onClick={() => startEdit(scope)} title="Edit">
                <PencilIcon size={13} />
              </button>
              <button type="button" className="aaa-icon-btn aaa-icon-btn-danger" onClick={() => setPendingDelete(scope)} title="Delete">
                <TrashIcon size={13} />
              </button>
            </div>
          </div>
        ))}
      </div>

      <ConfirmDialog
        isOpen={!!pendingDelete}
        title="Delete Scope"
        message={`Delete scope (type #${pendingDelete?.ScopeTypeID})?`}
        busy={deleting}
        onConfirm={handleDeleteConfirmed}
        onCancel={() => setPendingDelete(null)}
      />
    </div>
  );
}
